if (!customElements.get('sample-product-card')) {
  customElements.define(
    'sample-product-card',
    class SampleProductCard extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('.sample-product-card__form');
        this.button = this.querySelector('.sample-product-card__button');
        this.variantId = this.querySelector('[name="id"]')?.value || this.dataset.variantId;
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.helpButton = this.querySelector('.sample-product-card__help-button');
        this.helpPopover = this.querySelector('.sample-product-card__help-popover');
        this.errorMessage = this.querySelector('[data-sample-error]');
        this.onDocumentClick = this.closeHelpPopover.bind(this);

        if (this.form && this.button && this.variantId) {
          this.form.addEventListener('submit', this.onAddSample.bind(this));
        }

        if (this.helpButton && this.helpPopover) {
          this.helpButton.addEventListener('click', (event) => {
            event.stopPropagation();
            this.helpPopover.classList.toggle('hidden');
          });

          document.addEventListener('click', this.onDocumentClick);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      closeHelpPopover() {
        if (this.helpPopover) this.helpPopover.classList.add('hidden');
      }

      getSectionsToRender() {
        if (this.cart?.getSectionsToRender) return this.cart.getSectionsToRender();

        return [
          { id: 'cart-drawer' },
          { id: 'cart-icon-bubble' },
        ];
      }

      getSectionIds() {
        return this.getSectionsToRender().map((section) => section.id);
      }

      buildFormData() {
        const formData = new FormData(this.form);
        const sectionIds = this.getSectionIds();

        if (sectionIds.length > 0) {
          formData.set('sections', sectionIds.join(','));
          formData.set('sections_url', window.location.pathname);
        }

        return formData;
      }

      setLoading(isLoading) {
        const spinner = this.button?.querySelector('.loading__spinner');

        if (isLoading) {
          this.button?.classList.add('loading');
          this.button?.setAttribute('aria-disabled', 'true');
          if (spinner) spinner.classList.remove('hidden');
          return;
        }

        this.button?.classList.remove('loading');
        this.button?.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
      }

      showError(message = '') {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorMessage.classList.toggle('hidden', !message);
      }

      isSampleItem(item) {
        return Boolean(item?.properties?._is_sample === 'true');
      }

      async validateCart() {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

        const cart = await response.json();
        const items = Array.isArray(cart.items) ? cart.items : [];
        const hasRegularItem = items.some((item) => !this.isSampleItem(item));
        if (hasRegularItem) {
          this.showError(
            this.dataset.conflictMessage ||
              'Você já tem produtos no carrinho. Esvazie o carrinho antes de adicionar amostras.'
          );
          return false;
        }

        const existingSample = items.some(
          (item) =>
            this.isSampleItem(item) && String(item.variant_id || item.id || '') === String(this.variantId || '')
        );
        if (existingSample) {
          this.showError('Esta amostra já está no carrinho.');
          return false;
        }

        const sampleQuantity = items.reduce(
          (total, item) => total + (this.isSampleItem(item) ? Math.max(0, Number(item.quantity) || 0) : 0),
          0
        );
        if (sampleQuantity >= 5) {
          this.showError(this.dataset.limitMessage || 'Você atingiu o limite de 5 amostras por pedido.');
          return false;
        }

        this.showError('');
        return true;
      }

      async fetchCartSections() {
        const sectionIds = this.getSectionIds();
        if (sectionIds.length === 0) return null;

        const sectionsUrl = `${window.location.pathname}?sections=${sectionIds.join(',')}`;
        const response = await fetch(sectionsUrl, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) return null;

        return response.json();
      }

      async renderCart(addResponse) {
        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        this.cart.classList.remove('is-empty');

        try {
          if (this.cart.setActiveElement) this.cart.setActiveElement(document.activeElement);
          if (addResponse.sections && this.cart.renderContents) {
            this.cart.renderContents(addResponse);
            return;
          }
        } catch (error) {
          console.warn('Sample cart render failed. Trying section fallback.', error);
        }

        try {
          const sections = await this.fetchCartSections();
          if (sections && this.cart.renderContents) {
            this.cart.renderContents({
              ...addResponse,
              sections,
            });
            return;
          }
        } catch (error) {
          console.warn('Sample cart section fallback failed.', error);
        }

        window.location = window.routes.cart_url;
      }

      async onAddSample(event) {
        event.preventDefault();
        if (this.button?.classList.contains('loading')) return;

        this.setLoading(true);

        try {
          if (!(await this.validateCart())) {
            this.setLoading(false);
            return;
          }
        } catch (error) {
          console.warn('Sample cart validation failed.', error);
          this.showError(
            this.dataset.cartCheckMessage ||
              'Não foi possível verificar seu carrinho agora. Tente novamente em instantes.'
          );
          this.setLoading(false);
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = this.buildFormData();

        try {
          const response = await fetch(window.routes.cart_add_url, config);
          const parsedState = await response.json();

          if (parsedState.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'sample-product-card',
              productVariantId: this.variantId,
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
            console.error('Error adding sample:', parsedState.description || parsedState.message);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sample-product-card',
            productVariantId: this.variantId,
            cartData: parsedState,
          });

          await this.renderCart(parsedState);
        } catch (error) {
          console.error('Fetch error adding sample:', error);
          window.location = window.routes.cart_url;
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
